import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { IIdmRole, IIdmStaffListItem } from "../../../../entities/idm";
import { studentService } from '../../../../entities/student';
import { TagSelector } from '../../../../features/tagSelector';
import { MyInput } from "../../../../shared/ui/input";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { SwitchButton } from "../../../../shared/ui/switchButton";
import { ToggleSwitch } from "../../../../shared/ui/toggleSwitch";
import { LabelBadges, LabelsFilter } from '../../../../shared/ui/labels';
import { tutorService } from '../../../../entities/tutor';
import { OpenContainer } from '../../../../features/openContainer';
import lupaImg from "../../../../features/searchItems/lib/assets/lupa.png";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import classes from "../staff/staffWidget.module.scss";
import { StaffTutorsWidget } from "../staffTutors/StaffTutorsWidget";
import { IStaffTabActions, IStaffWorkTgFilterContext } from "../../model/types";

interface IStaffListContentProps {
    visible: boolean;
    canCreateStaff: boolean;
    canListRoles: boolean;
    canReadWorkTgs: boolean;
    isTutorRoleSelected: boolean;
    isTutorListOnlyMode: boolean;
    searchValue: string;
    setSearchValue: (value: string) => void;
    selectedWorkTgIds: number[];
    onWorkTgChange: (ids: number[]) => void;
    selectedTagIds: number[];
    onTagIdsChange: (ids: number[]) => void;
    renderWorkTgFilter?: (context: IStaffWorkTgFilterContext) => ReactNode;
    selectedRoleId: number | null;
    roles: IIdmRole[];
    tutorRoleId: number | null;
    tutorCount: number;
    tabActions: IStaffTabActions;
    onSelectRole: (roleId: number | null) => void;
    isStaffArchiveTab: boolean;
    setIsStaffArchiveTab: (value: boolean) => void;
    isTutorArchiveFilter: boolean;
    setIsTutorArchiveFilter: (value: boolean) => void;
    canUseWithoutTgFilter: boolean;
    isWithoutTgFilter: boolean;
    setIsWithoutTgFilter: (value: boolean) => void;
    tutorLabels: string[];
    onTutorLabelsChange: (labels: string[]) => void;
    isLowLoadFilter: boolean;
    setIsLowLoadFilter: (value: boolean) => void;
    isLoading: boolean;
    effectiveWorkTgIds: number[];
    getStaffPath: (userId: number | null) => string;
    setTutorCount: (count: number) => void;
    filteredStaff: Array<IIdmStaffListItem & {name: string}>;
    staffEmptyText: string;
    canOpenStaff: (userId: number) => boolean;
    onCreateStaff: () => void;
}

export const StaffListContent: FC<IStaffListContentProps> = ({
    visible,
    canCreateStaff,
    canListRoles,
    canReadWorkTgs,
    isTutorRoleSelected,
    isTutorListOnlyMode,
    searchValue,
    setSearchValue,
    selectedWorkTgIds,
    onWorkTgChange,
    selectedTagIds,
    onTagIdsChange,
    renderWorkTgFilter,
    selectedRoleId,
    roles,
    tutorRoleId,
    tutorCount,
    tabActions,
    onSelectRole,
    isStaffArchiveTab,
    setIsStaffArchiveTab,
    isTutorArchiveFilter,
    setIsTutorArchiveFilter,
    canUseWithoutTgFilter,
    isWithoutTgFilter,
    setIsWithoutTgFilter,
    tutorLabels,
    onTutorLabelsChange,
    isLowLoadFilter,
    setIsLowLoadFilter,
    isLoading,
    effectiveWorkTgIds,
    getStaffPath,
    setTutorCount,
    filteredStaff,
    staffEmptyText,
    canOpenStaff,
    onCreateStaff,
}) => {
    if(!visible){
        return null
    }

    const isTutorList = isTutorRoleSelected || isTutorListOnlyMode
    const getTabTitle = (title: string, count: number, isActive: boolean) => (
        isActive && count > 0 ? `${title} ${count}` : title
    )
    const allTabActive = selectedRoleId === null && !isStaffArchiveTab
    const archiveTabActive = isStaffArchiveTab

    return (
        <section className={classes.listPage}>
            <section className={idmClasses.topPanel}>
                <section className={classes.topBar}>
                    <section className={classes.titleRow}>
                        <section className={idmClasses.panelTitle}>{isTutorList ? (isTutorArchiveFilter ? 'Архив исполнителей' : 'Исполнители') : (isStaffArchiveTab ? 'Архив работников' : 'Работники')}</section>
                        {canCreateStaff && canListRoles && (
                            <button
                                className={idmClasses.createButton}
                                onClick={onCreateStaff}
                                aria-label={isTutorList ? 'Создать исполнителя' : 'Создать работника'}
                            >
                                +
                            </button>
                        )}
                    </section>
                    <section className={idmClasses.search}>
                        <MyInput
                            value={searchValue}
                            setValue={setSearchValue}
                            placeholder={isTutorList ? "Поиск по ФИО или ID" : "Поиск по ФИО, email или роли"}
                            iconSrc={lupaImg}
                        />
                    </section>
                    {(canReadWorkTgs || canUseWithoutTgFilter) && !isTutorList && (
                        <section className={classes.filtersRow}>
                            {!isStaffArchiveTab && (
                                <>
                                    {canReadWorkTgs && (
                                        <section className={classes.filterItem}>
                                            <section className={classes.filterLabel}>Фильтр по ТГ</section>
                                            {renderWorkTgFilter?.({
                                                selectedIds: selectedWorkTgIds,
                                                hideLabel: true,
                                                onChange: onWorkTgChange,
                                            })}
                                        </section>
                                    )}
                                    {canUseWithoutTgFilter && (
                                        <section className={classes.filterItem}>
                                            <section className={classes.filterLabel}>Без ТГ</section>
                                            <ToggleSwitch
                                                checked={isWithoutTgFilter}
                                                onSelected={setIsWithoutTgFilter}
                                            />
                                        </section>
                                    )}
                                </>
                            )}
                        </section>
                    )}
                    {!isTutorListOnlyMode && (
                        <section className={classes.tabs}>
                            <button
                                className={classes.tab + (allTabActive ? ` ${classes.active}` : '')}
                                onClick={tabActions.selectAll}
                            >
                                {getTabTitle('Работники', filteredStaff.length, allTabActive)}
                            </button>
                            {roles.map(role => {
                                const roleTabActive = selectedRoleId === role.id && !isTutorArchiveFilter
                                const roleCount = filteredStaff.length

                                return (
                                    <button
                                        key={role.id}
                                        className={classes.tab + (roleTabActive ? ` ${classes.active}` : '')}
                                        onClick={() => tabActions.selectRole(role.id)}
                                    >
                                        {getTabTitle(role.name, roleCount, roleTabActive)}
                                    </button>
                                )
                            })}
                            {tutorRoleId !== null && (
                                <button
                                    className={classes.tab + (isTutorRoleSelected && !isTutorArchiveFilter ? ` ${classes.active}` : '')}
                                    onClick={() => tabActions.selectRole(tutorRoleId)}
                                >
                                    {getTabTitle('Исполнители', tutorCount, isTutorRoleSelected && !isTutorArchiveFilter)}
                                </button>
                            )}
                            <button
                                className={classes.tab + (archiveTabActive ? ` ${classes.active}` : '')}
                                onClick={tabActions.selectStaffArchive}
                            >
                                {getTabTitle('Архив работников', filteredStaff.length, archiveTabActive)}
                            </button>
                        </section>
                    )}
                    {isTutorList && !isStaffArchiveTab && (
                        <section className={classes.tutorFiltersBlock}>
                            <section className={classes.tutorSwitchWrap}>
                                <SwitchButton
                                    text1="Исполнители"
                                    text2="Архив исполнителей"
                                    selected={isTutorArchiveFilter ? 2 : 1}
                                    onSelected={(index) => setIsTutorArchiveFilter(index === 2)}
                                />
                            </section>
                            <OpenContainer title="Фильтры" allowOverflow>
                                <section className={classes.tutorFiltersRow}>
                                    {canReadWorkTgs && (
                                        <section className={classes.tutorFilterItem}>
                                            {renderWorkTgFilter?.({
                                                selectedIds: selectedWorkTgIds,
                                                onChange: onWorkTgChange,
                                            })}
                                        </section>
                                    )}
                                    <section className={classes.tutorFilterItem}>
                                        <LabelsFilter
                                            value={tutorLabels}
                                            onChange={onTutorLabelsChange}
                                            loadOptions={tutorService.getLabels.bind(tutorService)}
                                            singleLine
                                        />
                                    </section>
                                    <section className={classes.tutorFilterItem}>
                                        <TagSelector
                                            title="Теги"
                                            singleLine
                                            selectedItems={selectedTagIds.map(id => ({id, name: String(id)}))}
                                            onChange={items => onTagIdsChange(items.map(item => Number(item.id)).filter(id => id > 0))}
                                            getData={async () => (await studentService.getTags()).map(tag => ({id: tag.id, name: tag.tag_name}))}
                                        />
                                    </section>
                                    <section className={classes.tutorToggle}>
                                        <span>Малая занятость:</span>
                                        <ToggleSwitch
                                            checked={isLowLoadFilter}
                                            onSelected={setIsLowLoadFilter}
                                        />
                                    </section>
                                    {canUseWithoutTgFilter && (
                                        <section className={classes.tutorToggle}>
                                            <span>Без ТГ:</span>
                                            <ToggleSwitch
                                                checked={isWithoutTgFilter}
                                                onSelected={setIsWithoutTgFilter}
                                            />
                                        </section>
                                    )}
                                </section>
                            </OpenContainer>
                        </section>
                    )}
                </section>
            </section>

            <section className={classes.listPanel}>
                {isLoading
                    ? <section className={idmClasses.loader}><LoaderSpinner /></section>
                    : isTutorList
                        ? <StaffTutorsWidget
                            isArchive={isTutorArchiveFilter}
                            workTgIds={effectiveWorkTgIds}
                            withoutTg={isWithoutTgFilter}
                            labels={tutorLabels}
                            isLowLoad={isLowLoadFilter}
                            tagIds={selectedTagIds}
                            getStaffPath={getStaffPath}
                            onCountChange={setTutorCount}
                            searchValue={searchValue}
                        />
                        : filteredStaff.length === 0
                            ? <section className={classes.emptyState}>{staffEmptyText}</section>
                            : <section className={classes.listGrid}>
                                {filteredStaff.map(item => {
                                    const content = <>
                                        <LabelBadges labels={item.labels} />
                                        <span className={idmClasses.staffName}>{item.user_id}. {item.full_name}</span>
                                        <span className={idmClasses.staffEmail}>{item.email}</span>
                                        <span className={idmClasses.staffRoles}>{item.roles.join(', ') || 'Без ролей'}</span>
                                    </>

                                    return canOpenStaff(item.user_id)
                                        ? <Link
                                            key={item.user_id}
                                            to={getStaffPath(item.user_id)}
                                            className={`${idmClasses.staffItem} staffLinkOverride`}
                                        >
                                            {content}
                                        </Link>
                                        : <section key={item.user_id} className={idmClasses.staffItem}>
                                            {content}
                                        </section>
                                })}
                            </section>
                }
            </section>
        </section>
    )
}
